// Kwaliteitstrap waarop het dashboard tekent: high (snel), mid (Pi die het bijhoudt), low (trage Pi).
// Keuze in volgorde: ?fx= in de URL (blijft bewaard), dan de meting (watchFrames), anders een gok.
// De CSS leest dezelfde trap via data-fx-tier op <html>.

export const tierOrder = ["high", "mid", "low"];

const settings = {
  high: {
    confettiBits: 170,
    burstSpots: 18,
    burstRings: 2,
    burstBits: 30,
    heroConfetti: 60,
    // Kleurbanen per schermbreedte in één schuivende strook, zie animations/party.js.
    partyBands: 8,
    // Elke lichtbundel is een eigen schermhoge laag, en daar raakt de trage Pi geheugen door kwijt.
    partyBeams: 6,
    // Snippers per hoekkanon; elke snipper is twee elementen, dus dit telt dubbel.
    partyCannonBits: 48,
    // "full": wijde en strakke gloed plus kop met blur, "light": alleen strakke gloed, "off": geen blur.
    gaugeGlow: "full",
    // Ademende gloed op een blur-element: de blur wordt elk frame herrekend, het duurste op een Pi.
    gaugeBreathe: true,
    // Scherven als de aanloop vol is; bij een grote deal het dubbele.
    chargeShards: 30,
    // Blokjes in de staart van het geld dat de meter in vliegt (target-feed).
    feedTrail: 5,
  },
  mid: {
    confettiBits: 80,
    burstSpots: 12,
    burstRings: 2,
    burstBits: 16,
    heroConfetti: 28,
    partyBands: 5,
    partyBeams: 4,
    partyCannonBits: 26,
    gaugeGlow: "light",
    gaugeBreathe: false,
    chargeShards: 16,
    feedTrail: 3,
  },
  low: {
    confettiBits: 38,
    burstSpots: 7,
    burstRings: 1,
    burstBits: 12,
    heroConfetti: 12,
    partyBands: 3,
    partyBeams: 3,
    partyCannonBits: 12,
    gaugeGlow: "off",
    gaugeBreathe: false,
    chargeShards: 8,
    feedTrail: 1,
  },
};

const storeKey = "tdFxTier";
const forcedKey = "tdFxTierForced";

// Opstartframes (React, font, eerste ophaalronde) zijn altijd traag, dus pas daarna meten.
const warmupMs = 1500;
const sampleSize = 45;
// Gemiddeld trager dan dit (ms per frame, ~42 fps) is niet vloeiend.
const slowFrameMs = 24;
// Zoveel zichtbare haperingen (frames boven 50ms) is ook een reden om te zakken.
const stutterLimit = 6;
const stutterFrameMs = 50;

let current = null;
let forced = false;
const listeners = new Set();

function read(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Kiosk zonder opslag: dan meten we elke keer opnieuw.
    return null;
  }
}

function write(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // De trap geldt dan alleen deze sessie.
  }
}

function forget() {
  write(forcedKey, "");
  write(storeKey, "");
}

function valid(tier) {
  return tierOrder.includes(tier) ? tier : null;
}

function announce(tier) {
  listeners.forEach((listener) => listener(tier));
}

// Gok op het apparaat: ARM (de Pi's) nooit op "high" voor we gemeten hebben.
function guessTier() {
  const cores = window.navigator.hardwareConcurrency || 4;
  const memory = window.navigator.deviceMemory || 0;
  const agent = (window.navigator.userAgent || "").toLowerCase();
  const arm = /aarch64|armv7|armv8|arm64|raspberry|linux arm/.test(agent);

  if (cores <= 2 || (memory && memory <= 2)) return "low";
  if (arm || cores <= 4) return "mid";
  return "high";
}

// ?fx=low zet de trap vast (ook na herstarten), ?fx=auto laat hem weer meten.
function readOverride() {
  try {
    const asked = new URLSearchParams(window.location.search).get("fx");
    if (!asked) return null;
    if (asked === "auto") {
      forget();
      return "auto";
    }
    const tier = valid(asked);
    if (tier) write(forcedKey, tier);
    return tier;
  } catch {
    return null;
  }
}

function resolve() {
  const override = readOverride();
  if (override === "auto") return guessTier();

  const pinned = override || valid(read(forcedKey));
  if (pinned) {
    forced = true;
    return pinned;
  }
  return valid(read(storeKey)) || guessTier();
}

function apply(tier) {
  current = tier;
  if (typeof document !== "undefined") {
    document.documentElement.dataset.fxTier = tier;
  }
}

// De eerste aanroep bepaalt de trap en zet hem op <html>.
export function renderTier() {
  if (!current) apply(resolve());
  return current;
}

export function fxSettings() {
  return settings[renderTier()];
}

export function isTierForced() {
  renderTier();
  return forced;
}

// Voor componenten die niet zelf opnieuw tekenen als de trap zakt, zie useRenderTier.
export function onTierChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setRenderTier(tier, { remember = true } = {}) {
  const next = valid(tier);
  if (!next || next === renderTier()) return;

  apply(next);
  if (remember) write(storeKey, next);
  announce(next);
}

// Met de hand vastzetten (animatielab, ?fx=): de meting laat hem dan met rust, ook na herstarten.
export function forceRenderTier(tier) {
  const next = valid(tier);
  if (!next) return;

  write(forcedKey, next);
  forced = true;
  apply(next);
  announce(next);
}

// Terug naar de meting: alles vergeten en opnieuw raden.
export function autoRenderTier() {
  forget();
  forced = false;

  const next = guessTier();
  apply(next);
  announce(next);
}

function stepDown() {
  const next = tierOrder[tierOrder.indexOf(renderTier()) + 1];
  if (next) setRenderTier(next);
}

// Meet één keer hoe vloeiend het beeld loopt en zakt een trapje als het niet meekomt.
// Aanroepen als het dashboard net staat en tijdens animaties; geeft een opruimfunctie terug.
export function watchFrames() {
  if (
    typeof window === "undefined" ||
    isTierForced() ||
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  ) {
    return () => {};
  }

  let frame = 0;
  let last = 0;
  let count = 0;
  let total = 0;
  let stutters = 0;
  // -1: de eerste tijdstempel mag zelf 0 zijn.
  let started = -1;
  let stopped = false;

  const step = (now) => {
    if (stopped) return;
    if (started < 0) started = now;

    if (now - started < warmupMs) {
      last = now;
      frame = requestAnimationFrame(step);
      return;
    }

    if (last) {
      const delta = now - last;
      // Een sprong boven 500ms is een tabblad dat weg was, geen hapering.
      if (delta < 500) {
        count += 1;
        total += delta;
        if (delta > stutterFrameMs) stutters += 1;
      }
    }
    last = now;

    if (count >= sampleSize) {
      if (total / count > slowFrameMs || stutters >= stutterLimit) stepDown();
      return; // klaar: één meting per aanroep
    }

    frame = requestAnimationFrame(step);
  };

  frame = requestAnimationFrame(step);

  return () => {
    stopped = true;
    cancelAnimationFrame(frame);
  };
}
